import { useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const COMPANY_META = {
  musicmagpie: { label: 'MusicMagpie', url: 'https://www.musicmagpie.co.uk/sell/instant-valuation', expectedHint: '£1.50' },
  webuybooks: { label: 'WeBuyBooks', url: 'https://www.webuybooks.co.uk', expectedHint: '£1.40' },
  worldofbooks: { label: 'World of Books', url: 'https://www.wob.com/sell', expectedHint: '£0.60' },
  cex: { label: 'CEX', url: 'https://uk.webuy.com/sell', expectedHint: null }, // no sacrifice
};

/**
 * Phase 2 — user pastes sacrifice barcodes into each company website,
 * confirms the total they see matches our expectation (±£0.05).
 * CEX never needs sacrifice (no price inflation).
 */
export default function SacrificePhase({ batch, onConfirmed, onCancel }) {
  const companiesWithSacrifice = batch.companies.filter((c) => c !== 'cex');
  const [totals, setTotals] = useState(
    Object.fromEntries(companiesWithSacrifice.map((c) => [c, '']))
  );
  const [err, setErr] = useState('');
  const [mismatches, setMismatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedCo, setCopiedCo] = useState('');

  const cexOnly = batch.companies.length === 1 && batch.companies[0] === 'cex';
  // When the admin pool is empty, ALL non-CEX companies come back with zero barcodes. In that
  // case the sacrifice step cannot be performed, so we let the user skip it with one click
  // (the backend also tolerates this path — see POST /confirm-sacrifice with totals: {}).
  const allEmpty =
    companiesWithSacrifice.length > 0 &&
    companiesWithSacrifice.every(
      (c) => (batch.sacrifice_barcodes?.[c] || []).length === 0
    );

  const copyBarcodes = (company) => {
    const list = (batch.sacrifice_barcodes[company] || []).join('\n');
    navigator.clipboard.writeText(list);
    setCopiedCo(company);
    setTimeout(() => setCopiedCo(''), 1800);
  };

  const submit = async () => {
    setErr('');
    setMismatches([]);
    setLoading(true);
    try {
      const parsed = {};
      for (const c of companiesWithSacrifice) {
        // If pool is empty for this company, skip it — backend will also skip when
        // sacrifice_barcodes[c] is [].
        if ((batch.sacrifice_barcodes?.[c] || []).length === 0) continue;
        const v = parseFloat(totals[c]);
        if (isNaN(v)) {
          setErr(`Enter the basket total you saw on ${COMPANY_META[c].label}.`);
          setLoading(false);
          return;
        }
        parsed[c] = v;
      }
      const res = await fetch(`${API}/api/batches/${batch.batch_id}/confirm-sacrifice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ totals: parsed }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErr(data.detail || 'Could not confirm sacrifice.');
      } else if (!data.success) {
        setMismatches(data.mismatches || []);
        setErr(data.hint || 'Totals do not match expected values.');
      } else {
        onConfirmed();
      }
    } finally {
      setLoading(false);
    }
  };

  if (cexOnly) {
    // Skip sacrifice entirely
    return (
      <div data-testid="sacrifice-skip-cex" className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
        <h3 className="text-lg font-bold text-white mb-2">No sacrifice needed for CEX</h3>
        <p className="text-sm text-zinc-400 mb-5">CEX does not inflate early-basket prices. You can skip straight to submitting prices.</p>
        <button
          onClick={async () => {
            setLoading(true);
            await fetch(`${API}/api/batches/${batch.batch_id}/confirm-sacrifice`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ totals: {} }),
            });
            setLoading(false);
            onConfirmed();
          }}
          className="px-6 py-3 rounded-full font-bold"
          style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
          data-testid="sacrifice-skip-btn"
        >
          {loading ? 'Skipping…' : 'Continue to price entry →'}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="sacrifice-phase">
      <div className="bg-[#FFD700]/10 border border-[#FFD700]/40 rounded-xl p-4 mb-5">
        <p className="text-sm leading-relaxed">
          <strong className="text-[#FFD700]">Sacrifice step — why this matters.</strong>{' '}
          The buyer websites <em>inflate</em> prices on your first few items to tempt you to fill
          the basket. If you submit your real barcodes first, those inflated prices pollute our
          database with false data. So you paste our sacrifice barcodes first, confirm the expected
          totals below, then we know the next prices you see are truthful.
        </p>
      </div>

      {batch.sacrifice_degraded && (
        <div className="bg-[#E04A3F]/10 border border-[#E04A3F]/40 rounded-lg p-3 mb-4 text-xs text-zinc-300" data-testid="sacrifice-degraded-banner">
          <strong className="text-[#E04A3F]">Admin notice:</strong> sacrifice pool is short —
          calibration integrity is degraded for this batch. Add more verified pool barcodes before launch.
        </div>
      )}

      {allEmpty && (
        <div
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-5 text-center"
          data-testid="sacrifice-all-empty"
        >
          <p className="text-sm text-zinc-300 mb-4">
            No calibration barcodes are available for these companies yet. You can skip calibration
            for this batch — prices you submit will still save, but they won&apos;t be guaranteed
            free of early-basket inflation.
          </p>
          <button
            onClick={submit}
            disabled={loading}
            className="px-6 py-2 rounded-full font-bold text-sm disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
            data-testid="sacrifice-skip-empty-btn"
          >
            {loading ? 'Skipping…' : 'Skip calibration & continue →'}
          </button>
        </div>
      )}

      {!allEmpty && companiesWithSacrifice.map((c) => {
        const meta = COMPANY_META[c];
        const barcodes = batch.sacrifice_barcodes[c] || [];
        const mismatch = mismatches.find((m) => m.company === c);
        const emptyForCo = barcodes.length === 0;
        return (
          <div
            key={c}
            className={`bg-zinc-900 border rounded-xl p-5 mb-4 ${
              mismatch ? 'border-[#E04A3F]' : 'border-zinc-800'
            }`}
            data-testid={`sacrifice-card-${c}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-bold text-white">{meta.label}</h3>
                {emptyForCo ? (
                  <p className="text-xs text-zinc-500">No calibration barcodes available for this company yet — skipped for this batch.</p>
                ) : (
                  <p className="text-xs text-zinc-500">
                    Paste these {barcodes.length} barcodes · expected total{' '}
                    <strong className="text-[#FFD700]">{meta.expectedHint}</strong>
                  </p>
                )}
              </div>
              {!emptyForCo && (
                <a
                  href={meta.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-bold text-[#00D26A] underline"
                  data-testid={`sacrifice-open-${c}`}
                >
                  Open site →
                </a>
              )}
            </div>

            {emptyForCo ? (
              <div className="text-xs text-zinc-500 italic">
                Skipping calibration for {meta.label} on this batch.
              </div>
            ) : (
              <>
                <div className="bg-black rounded-lg p-3 max-h-32 overflow-auto text-xs font-mono text-zinc-300 mb-2">
                  {barcodes.map((b) => (
                    <div key={b} data-testid={`sac-bc-${c}-${b}`}>
                      {b}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => copyBarcodes(c)}
                    className="px-3 py-2 rounded-full text-xs font-bold border border-zinc-700 hover:bg-zinc-800"
                    data-testid={`sacrifice-copy-${c}`}
                  >
                    {copiedCo === c ? '✓ Copied' : `📋 Copy ${barcodes.length} barcodes`}
                  </button>
                </div>
                <label className="block text-xs text-zinc-400 mb-1">
                  Basket total shown on {meta.label}:
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500">£</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={totals[c]}
                    onChange={(e) => setTotals({ ...totals, [c]: e.target.value })}
                    placeholder={meta.expectedHint?.replace('£', '')}
                    className="flex-1 bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white font-mono focus:outline-none focus:border-[#00D26A]"
                    data-testid={`sacrifice-total-${c}`}
                  />
                  {mismatch && (
                    <span className="text-xs font-bold" style={{ color: '#E04A3F' }}>
                      Expected £{mismatch.expected.toFixed(2)}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}

      {err && (
        <p className="text-sm mb-3" style={{ color: '#E04A3F' }} data-testid="sacrifice-error">
          {err}
        </p>
      )}

      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-full font-bold text-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          data-testid="sacrifice-cancel-btn"
        >
          Cancel batch
        </button>
        {!allEmpty && (
          <button
            onClick={submit}
            disabled={loading}
            className="px-6 py-2 rounded-full font-bold text-sm disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
            data-testid="sacrifice-confirm-btn"
          >
            {loading ? 'Checking…' : 'Confirm & continue →'}
          </button>
        )}
      </div>
    </div>
  );
}
